import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check, LayoutGrid, HeartHandshake, Compass } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useDocumentMeta } from "@/lib/seo";

const PETROLE = "#284E55";
const TERRECUITE = "#8A5541";

const PARTNER_FEATURES = [
  "Gestion des prospects, des clients et des projets",
  "Outils de suivi de chantier",
  "Collaboration avec les entreprises du réseau",
  "Accès aux services techniques CozyHome : plans, déclarations préalables, permis de construire, 3D",
  "Outils de communication et ressources professionnelles",
  "Secteur attribué après validation",
];

const PARTNER_STEPS = [
  "Création du compte",
  "Dossier professionnel",
  "Vérification CozyHome",
  "Validation",
  "Souscription à l'abonnement",
  "Activation du secteur",
  "Accès complet à l'Espace Partenaire",
];

const COMPANY_FEATURES = [
  "Collaborer sur les projets CozyHome",
  "Accéder aux chantiers auxquels elle est affectée",
  "Recevoir les informations nécessaires à ses interventions",
  "Consulter et répondre aux observations chantier",
  "Ajouter des photos d'avancement",
  "Transmettre ses devis et ses factures, et suivre le traitement de ses documents",
];

const FeatureList = ({ items, color, testid }) => (
  <ul data-testid={testid} className="mt-7 space-y-3.5">
    {items.map((f) => (
      <li key={f} className="flex gap-3 text-sm leading-relaxed text-stone-600 sm:text-base">
        <span
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${color}1A`, color }}
        >
          <Check className="h-3 w-3" />
        </span>
        {f}
      </li>
    ))}
  </ul>
);

export default function NousRejoindrePage() {
  const navigate = useNavigate();
  const location = useLocation();

  useDocumentMeta(
    "Nous rejoindre — Devenir Partenaire CozyHome ou entreprise du réseau | CozyHome",
    "CozyHome rassemble des maîtres d'œuvre indépendants et des entreprises du bâtiment. Découvrez l'offre Partenaire CozyHome et le réseau d'entreprises."
  );

  // Défilement vers l'ancre #partenaire / #entreprise (liens du menu Connexion)
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash]);

  return (
    <PageShell>
      <PageHero
        testid="page-rejoindre-hero"
        label="Nous rejoindre"
        title={<>Rejoignez <span className="italic text-sage">l'écosystème CozyHome.</span></>}
        sub="CozyHome rassemble des maîtres d'œuvre indépendants et des entreprises du bâtiment autour d'une même ambition : rendre les projets de rénovation, d'aménagement, d'extension et de construction plus simples à préparer et à réaliser."
      />

      <section className="bg-ivory pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl space-y-10 px-5 sm:px-8">
          {/* ---------------- Carte Partenaire ---------------- */}
          <Reveal>
            <article
              id="partenaire"
              data-testid="rejoindre-card-partenaire"
              className="scroll-mt-28 overflow-hidden rounded-3xl bg-white"
            >
              <div className="h-1.5 w-full" style={{ backgroundColor: PETROLE }} aria-hidden="true" />
              <div className="p-7 sm:p-12">
                <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>
                  Partenaire CozyHome
                </p>
                <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                  Devenir Partenaire CozyHome
                </h2>
                <p className="mt-4 max-w-2xl font-display text-lg italic leading-relaxed text-stone-500 sm:text-xl">
                  Vous êtes maître d'œuvre indépendant ou souhaitez développer votre activité
                  de maîtrise d'œuvre ?
                </p>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                  L'environnement CozyHome vous donne un cadre complet pour piloter votre
                  activité, de la première prise de contact jusqu'à la réception des travaux.
                </p>

                <div className="mt-2 grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
                  <div className="lg:col-span-3">
                    <FeatureList items={PARTNER_FEATURES} color={PETROLE} testid="partenaire-features" />

                    <div className="mt-10 rounded-2xl bg-cream p-6 sm:p-7">
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-stone-400">Le parcours</p>
                      <ol data-testid="partenaire-steps" className="mt-4 flex flex-wrap gap-2">
                        {PARTNER_STEPS.map((s, i) => (
                          <li key={s} className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-2 rounded-full border border-[#E4DFD5] bg-white px-3.5 py-2 text-xs font-semibold text-navy">
                              <span className="font-display text-sm font-medium" style={{ color: PETROLE }}>
                                {String(i + 1).padStart(2, "0")}
                              </span>
                              {s}
                            </span>
                            {i < PARTNER_STEPS.length - 1 && (
                              <ArrowRight className="h-3.5 w-3.5 text-stone-300" aria-hidden="true" />
                            )}
                          </li>
                        ))}
                      </ol>
                      <p className="mt-4 text-xs leading-relaxed text-stone-500">
                        La souscription à l'abonnement intervient uniquement après vérification et validation du
                        dossier professionnel par CozyHome. Après souscription, le secteur attribué est activé et
                        le partenaire bénéficie d'un accès complet à son Espace Partenaire.
                      </p>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <div className="flex h-full flex-col rounded-2xl p-7 sm:p-8" style={{ backgroundColor: `${PETROLE}0D` }}>
                      <p className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: PETROLE }}>
                        Abonnement Partenaire
                      </p>
                      <p data-testid="partenaire-price" className="mt-4 font-display text-4xl font-medium tracking-tight text-navy sm:text-5xl">
                        249 € <span className="text-xl text-stone-500 sm:text-2xl">HT / mois</span>
                      </p>
                      <p className="mt-4 text-xs leading-relaxed text-stone-500">
                        Les prestations techniques commandées auprès de CozyHome Central sont
                        facturées séparément.
                      </p>
                      <div className="mt-auto pt-8">
                        <button
                          data-testid="partenaire-cta"
                          onClick={() => navigate("/devenir-partenaire")}
                          className="group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90"
                          style={{ backgroundColor: PETROLE }}
                        >
                          Découvrir l'offre Partenaire
                          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                          data-testid="partenaire-candidater-link"
                          onClick={() => navigate("/nous-rejoindre/partenaire")}
                          className="mt-3 flex items-center gap-2 text-sm font-bold underline-offset-4 hover:underline"
                          style={{ color: PETROLE }}
                        >
                          Candidater pour mon secteur
                          <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>

          {/* ---------------- Positionnement Partenaire ---------------- */}
          <Reveal>
            <section
              data-testid="rejoindre-partenaire-promesse"
              className="overflow-hidden rounded-3xl p-7 text-white sm:p-12"
              style={{ backgroundColor: PETROLE }}
            >
              <div className="grid gap-10 lg:grid-cols-5 lg:gap-14">
                <div className="lg:col-span-3">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac-light">La promesse Partenaire</p>
                  <h2 className="mt-4 font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-5xl">
                    Concentrez-vous sur le client.
                    <br />
                    <span className="italic text-white/80">CozyHome simplifie le reste.</span>
                  </h2>
                  <div className="mt-6 space-y-4 text-sm leading-relaxed text-white/80 sm:text-base">
                    <p>
                      Développez votre activité de maîtrise d'œuvre sans avoir à tout construire seul.
                      CozyHome vous apporte une méthode, des outils et un écosystème pour simplifier votre quotidien.
                    </p>
                    <p>
                      Vous vous concentrez sur l'essentiel : la relation client, le développement commercial
                      et le pilotage local des projets.
                    </p>
                    <p>
                      CozyHome vous accompagne avec les outils de gestion, les services techniques disponibles à la
                      commande, les ressources professionnelles et un environnement conçu pour réduire les tâches
                      chronophages.
                    </p>
                  </div>
                  <p data-testid="partenaire-phrase-forte" className="mt-8 font-display text-2xl font-medium italic text-cognac-light sm:text-3xl">
                    « Moins de tracas. Plus de temps pour vos clients et vos projets. »
                  </p>
                </div>

                <div className="flex flex-col gap-3 lg:col-span-2">
                  {[
                    { icon: LayoutGrid, title: "Simple à prendre en main", text: "Un espace conçu pour organiser prospects, clients et projets sans multiplier les outils." },
                    { icon: HeartHandshake, title: "Vous n'êtes pas seul", text: "Méthode CozyHome, services techniques, outils, communication et écosystème professionnel." },
                    { icon: Compass, title: "Vous restez indépendant", text: "Vous développez votre propre activité de maître d'œuvre au sein de l'écosystème CozyHome." },
                  ].map((b) => (
                    <div key={b.title} data-testid="partenaire-bloc" className="rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cognac/25 text-cognac-light">
                        <b.icon className="h-4 w-4" />
                      </span>
                      <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em]">{b.title}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/75">{b.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-4 border-t border-white/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
                  Vous avez le sens du commerce, de l'organisation et de la relation client ?
                  Découvrez comment développer votre activité avec CozyHome.
                </p>
                <button
                  data-testid="partenaire-cta-secondaire"
                  onClick={() => navigate("/devenir-partenaire")}
                  className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-sm font-bold transition-colors duration-300 hover:bg-cream"
                  style={{ color: PETROLE }}
                >
                  Découvrir l'offre Partenaire
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </section>
          </Reveal>

          {/* ---------------- Carte Entreprise ---------------- */}
          <Reveal>
            <article
              id="entreprise"
              data-testid="rejoindre-card-entreprise"
              className="scroll-mt-28 overflow-hidden rounded-3xl bg-white"
            >
              <div className="h-1.5 w-full" style={{ backgroundColor: TERRECUITE }} aria-hidden="true" />
              <div className="p-7 sm:p-12">
                <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: TERRECUITE }}>
                  Entreprise du réseau
                </p>
                <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                  Rejoindre CozyHome en tant qu'entreprise
                </h2>
                <p className="mt-4 max-w-2xl font-display text-lg italic leading-relaxed text-stone-500 sm:text-xl">
                  Vous êtes artisan ou dirigeant d'une entreprise du bâtiment ?
                </p>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                  CozyHome vous ouvre l'accès à des projets suivis et cadrés. Votre entreprise
                  reste indépendante : elle collabore sur les chantiers qui lui sont confiés,
                  avec les informations et les outils adaptés.
                </p>

                <div className="mt-2 grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
                  <div className="lg:col-span-3">
                    <FeatureList items={COMPANY_FEATURES} color={TERRECUITE} testid="entreprise-features" />
                  </div>

                  <div className="lg:col-span-2">
                    <div className="flex h-full flex-col rounded-2xl p-7 sm:p-8" style={{ backgroundColor: `${TERRECUITE}0F` }}>
                      <p className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: TERRECUITE }}>
                        Un cadre clair pour chaque chantier
                      </p>
                      <p className="mt-4 text-sm leading-relaxed text-stone-600">
                        Affectation aux chantiers, informations d'intervention, échanges sur
                        les observations, transmission des documents : tout est réuni pour
                        que vos équipes avancent dans de bonnes conditions.
                      </p>
                      <div className="mt-auto pt-8">
                        <button
                          data-testid="entreprise-cta"
                          onClick={() => navigate("/nous-rejoindre/entreprise")}
                          className="group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90"
                          style={{ backgroundColor: TERRECUITE }}
                        >
                          Rejoindre le réseau d'entreprises
                          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
